import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { toast } from "sonner";
import type {
  AdminUser,
  AdminUserDetail,
  AdminCustomBot,
  AdminCustomBotDetail,
} from "@/lib/api/types";

export const adminKeys = {
  all: ["admin"] as const,
  users: (params?: { search?: string; offset?: number; limit?: number }) =>
    [...adminKeys.all, "users", params] as const,
  userDetail: (userId: string) =>
    [...adminKeys.all, "users", "detail", userId] as const,
  onlineUsers: () => [...adminKeys.all, "online-users"] as const,
  customBots: (params?: { search?: string; offset?: number; limit?: number }) =>
    [...adminKeys.all, "custom-bots", params] as const,
  customBotDetail: (applicationId: string) =>
    [...adminKeys.all, "custom-bots", "detail", applicationId] as const,
  notifications: (params?: { offset?: number; limit?: number }) =>
    [...adminKeys.all, "notifications", params] as const,
};

export const adminUsersQueryOptions = (params: {
  search?: string;
  offset?: number;
  limit?: number;
}) =>
  queryOptions({
    queryKey: adminKeys.users(params),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      if (params.search) searchParams.set("search", params.search);
      if (params.offset != null)
        searchParams.set("offset", String(params.offset));
      if (params.limit != null) searchParams.set("limit", String(params.limit));
      return apiClient
        .get<{
          data: AdminUser[];
          total: number;
        }>(`/api/admin/users?${searchParams.toString()}`)
        .then((r) => r.data);
    },
    placeholderData: keepPreviousData,
  });

export const adminUserDetailQueryOptions = (userId: string) =>
  queryOptions({
    queryKey: adminKeys.userDetail(userId),
    queryFn: () =>
      apiClient
        .get<{ data: AdminUserDetail }>(`/api/admin/users/${userId}`)
        .then((r) => r.data.data),
    enabled: !!userId,
  });

export function useAdminUpdateUser(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { name?: string; email?: string; isAdmin?: boolean }) =>
      apiClient.patch(`/api/admin/users/${userId}`, body).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useAdminUpdateUserPlan(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { plan: "free" | "pro" | "unlimited" }) =>
      apiClient
        .patch(`/api/admin/users/${userId}/plan`, body)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.userDetail(userId) });
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export function useAdminDeleteUser(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiClient.delete(`/api/admin/users/${userId}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}

export const adminOnlineUsersQueryOptions = () =>
  queryOptions({
    queryKey: adminKeys.onlineUsers(),
    queryFn: () =>
      apiClient
        .get<{ data: string[]; count: number }>("/api/admin/online-users")
        .then((r) => r.data),
    refetchInterval: 30_000,
  });

export const adminCustomBotsQueryOptions = (params: {
  search?: string;
  offset?: number;
  limit?: number;
}) =>
  queryOptions({
    queryKey: adminKeys.customBots(params),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      if (params.search) searchParams.set("search", params.search);
      if (params.offset != null)
        searchParams.set("offset", String(params.offset));
      if (params.limit != null) searchParams.set("limit", String(params.limit));
      return apiClient
        .get<{
          data: AdminCustomBot[];
          total: number;
        }>(`/api/admin/custom-bots?${searchParams.toString()}`)
        .then((r) => r.data);
    },
    placeholderData: keepPreviousData,
  });

export const adminCustomBotDetailQueryOptions = (applicationId: string) =>
  queryOptions({
    queryKey: adminKeys.customBotDetail(applicationId),
    queryFn: () =>
      apiClient
        .get<{
          data: AdminCustomBotDetail;
        }>(`/api/admin/custom-bots/${applicationId}`)
        .then((r) => r.data.data),
    enabled: !!applicationId,
  });

export function useAdminBotAction(applicationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (action: "start" | "stop" | "restart" | "redeploy") =>
      apiClient
        .post(`/api/admin/custom-bots/${applicationId}/${action}`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.customBotDetail(applicationId),
      });
    },
  });
}

// ── Notifications ──

interface AdminNotification {
  id: number;
  scope: string;
  type: string;
  title: string;
  message: string | null;
  metadata: Record<string, unknown>;
  isRead: boolean;
  createdAt: string;
  applicationGuildId: string | null;
  userId: string | null;
  tenantId: string | null;
}

export const adminNotificationsQueryOptions = (params: {
  offset?: number;
  limit?: number;
}) =>
  queryOptions({
    queryKey: adminKeys.notifications(params),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      if (params.offset != null)
        searchParams.set("offset", String(params.offset));
      if (params.limit != null) searchParams.set("limit", String(params.limit));
      return apiClient
        .get<{
          data: AdminNotification[];
          total: number;
        }>(`/api/admin/notifications?${searchParams.toString()}`)
        .then((r) => r.data);
    },
    placeholderData: keepPreviousData,
  });

export function useAdminSendNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: {
      scope: "user" | "tenant" | "broadcast";
      targetId?: string;
      type: string;
      title: string;
      message?: string;
    }) => apiClient.post("/api/admin/notifications", body).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminKeys.notifications(),
      });
      toast.success("Notification sent");
    },
    onError: () => {
      toast.error("Failed to send notification");
    },
  });
}
